/**
 * One URL per page, and its language versions [F35 step 3].
 *
 * The router accepts every page with and without a locale prefix (`/:locale(lv|en|ru)?`), so
 * `/pricing`, `/lv/pricing` and `/en/pricing` all render. Without a canonical URL Google sees copies.
 *
 * - Main site pages exist in three languages: the canonical is the page's own language version
 *   (`/lv/x` → `/x`, because lv has no prefix), and `hreflang` links name all three.
 * - Studio pages are always shown in Latvian (the router guard redirects `/en/join/x` to `/join/x`),
 *   so they have one URL and no language versions: canonical without prefix, no `hreflang`.
 *
 * Read by App.vue (vue-meta) and by the Cloudflare Pages Function, so the browser and the server
 * write the same URLs. Keep it free of imports that need webpack or Vue.
 */
import { SUPPORTED_LOCALES, DEFAULT_LOCALE_CODE } from '../../locales/supportedLocales.js'
import { ORGANIZATION_ROOT_SEGMENT } from '../../router/organizationPaths.js'

/** `hreflang` value for the version shown to languages we do not have. */
export const HREFLANG_X_DEFAULT = 'x-default'

/**
 * @param {string} pathname URL path, e.g. `/en/pricing/`
 * @returns {{ localeCode: string, path: string }} the language and the path without its prefix,
 *   with no trailing slash (`/` for the home page)
 */
export function splitLocale (pathname) {
  const segments = pathname.split('/').filter(Boolean)
  const prefixed = SUPPORTED_LOCALES.find(locale => locale.code === segments[0])
  if (prefixed) segments.shift()
  return {
    localeCode: prefixed ? prefixed.code : DEFAULT_LOCALE_CODE,
    path: '/' + segments.join('/')
  }
}

export function isStudioPath (path) {
  return path.split('/').filter(Boolean)[0] === ORGANIZATION_ROOT_SEGMENT
}

/** @returns {string} canonical path, e.g. `/en/pricing`, `/pricing`, `/join/studio` */
export function canonicalPath (pathname) {
  const { localeCode, path } = splitLocale(pathname)
  return isStudioPath(path) ? path : localizedPath(localeCode, path)
}

/**
 * @returns {Array<{ hreflang: string, path: string }>} empty for a studio page
 */
export function alternatePaths (pathname) {
  const { path } = splitLocale(pathname)
  if (isStudioPath(path)) return []
  return [
    ...SUPPORTED_LOCALES.map(locale => ({ hreflang: locale.code, path: localizedPath(locale.code, path) })),
    { hreflang: HREFLANG_X_DEFAULT, path: localizedPath(DEFAULT_LOCALE_CODE, path) }
  ]
}

/** `https://www.asport.lv/` + `/en/pricing` → `https://www.asport.lv/en/pricing`. */
export function absoluteUrl (siteUrl, path) {
  return siteUrl.replace(/\/+$/, '') + path
}

function localizedPath (localeCode, path) {
  const base = SUPPORTED_LOCALES.find(locale => locale.code === localeCode).base
  if (!base) return path
  return path === '/' ? base : base + path
}
